import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import {
  IconArrowLeft,
  IconArrowRight,
  ImageMultiply,
  Tooltip,
} from '@/components/atoms';
import {
  CARD_VARIANT_THUMBNAIL_SIZE,
  isVariantWithCustomColor,
} from '@/utils/Product';
import { IProductColorSwitcherCmp } from './IProductColorSwitcherCmp';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import 'swiper/swiper-bundle.css';
import useElementSize from '@/hooks/useElementSize';
import { useTranslation } from 'next-i18next';
import { debounce } from '@/utils/index';

type StyleProps = {
  highlighted?: boolean;
  navTop?: number;
};

const Container = styled.div<StyleProps>`
  position: relative;
  .swiper-slide {
    width: 100px;
  }

  .swiper-container {
    padding: 2px;
    /* padding-bottom: 16px; */
  }

  .swiper-scrollbar {
    border-radius: 0px;
    background: ${props => props.theme.cardSlider.backgroundScrollbar};
  }
  .swiper-scrollbar-drag {
    background: ${props => props.theme.cardSlider.colorScrollbar};
    border-radius: 0px;
  }
  .nav {
    position: absolute;
    top: ${props => props.navTop}px;
    cursor: pointer;
    z-index: 1;
    padding-top: 8px; // to expande arrow clickable area
    padding-bottom: 8px; // to expande arrow clickable area
  }
  .navPrev {
    left: 0;
    padding-right: 8px; // to expande arrow clickable area
  }
  .navNext {
    right: 0;
    padding-left: 8px; // to expande arrow clickable area
  }
`;

const VariantThumb = styled.div`
  width: 100px;
  padding-top: calc((5 / 4) * 100px);
  cursor: pointer;
  position: relative;
`;

const VariantImageWrapper = styled(VariantThumb)<StyleProps>`
  border: ${props =>
    props.highlighted
      ? `1.5px solid ${props.theme.paletteDefault.primary.main} `
      : 'none'};
  background-color: ${props => props.theme.productCard.backgroundColor};
`;

const CustomColorIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const ArrowWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.paletteDefault.white};
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const ProductColorSwitcher: React.FC<IProductColorSwitcherCmp> = ({
  selectedHandle,
  variantAttributes,
}) => {
  const { t } = useTranslation('common');
  const [imgRef, { height }] = useElementSize();

  const disableTooltip = true;

  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
  const [arrows, setArrows] = useState({
    left: false,
    right: false,
  });

  const updateArrowsCallback = (swiper: SwiperCore) => {
    const { isBeginning, isEnd } = swiper;
    setArrows({
      left: !isBeginning,
      right: !isEnd,
    });
  };
  const onSlideChange = useCallback(debounce(updateArrowsCallback, 400), []);

  useEffect(() => {
    if (swiperInstance) {
      updateArrowsCallback(swiperInstance);
    }
  }, [swiperInstance]);

  const variantImages =
    variantAttributes && variantAttributes.length > 0
      ? variantAttributes.map((va, index) => {
          const resizedImgUrl =
            va.image_url + `&width=${CARD_VARIANT_THUMBNAIL_SIZE}`;
          const isCustomColor = isVariantWithCustomColor(va.handle);

          const tooltipTitle = disableTooltip ? (
            ''
          ) : isCustomColor ? (
            <>{t('common:custom')}</>
          ) : (
            <>{va.color}</>
          );

          return (
            <SwiperSlide key={`thumb-${va.handle}-${index}`}>
              <Link
                href={{
                  pathname: '/products/[handle]',
                  query: { handle: va.handle },
                }}
                passHref>
                <Tooltip title={tooltipTitle} placement="bottom">
                  <VariantImageWrapper
                    highlighted={va.handle === selectedHandle}
                    ref={imgRef}>
                    <ImageMultiply>
                      <a>
                        {isCustomColor ? (
                          <CustomColorIcon>
                            <Image
                              alt="product variant thumbnail"
                              src={'/asset/customColorThumbnail.svg'}
                              width={48}
                              height={48}
                              unoptimized={true}
                            />
                          </CustomColorIcon>
                        ) : (
                          <Image
                            alt="product variant thumbnail"
                            src={resizedImgUrl}
                            layout="fill"
                            unoptimized={true}
                          />
                        )}
                      </a>
                    </ImageMultiply>
                  </VariantImageWrapper>
                </Tooltip>
              </Link>
            </SwiperSlide>
          );
        })
      : [];
  const goNext = () => {
    swiperInstance?.slideNext();
  };

  const goPrev = () => {
    swiperInstance?.slidePrev();
  };
  const navTop = height / 2 - 18; // 18 is half the size (in px) of the div containing the arrow
  const initialSlideIndex = variantAttributes
    ? variantAttributes.findIndex(va => va.handle === selectedHandle)
    : 0;
  return (
    <Container navTop={navTop}>
      <Swiper
        initialSlide={initialSlideIndex >= 0 ? initialSlideIndex : 0}
        onResize={onSlideChange}
        onTransitionEnd={onSlideChange}
        touchEventsTarget={'container'}
        grabCursor={true}
        slidesPerView={'auto'}
        spaceBetween={10}
        onSwiper={setSwiperInstance}>
        {variantImages}
      </Swiper>
      {height > 0 && arrows.left && (
        <div className="nav navPrev" onClick={goPrev}>
          <ArrowWrapper>
            <IconArrowLeft width={20} height={20} />
          </ArrowWrapper>
        </div>
      )}
      {height > 0 && arrows.right && (
        <div className="nav navNext" onClick={goNext}>
          <ArrowWrapper>
            <IconArrowRight width={20} height={20} />
          </ArrowWrapper>
        </div>
      )}
    </Container>
  );
};

export default ProductColorSwitcher;
